import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import { useAuth } from "../context/AuthContext";
import {
  useAvailableBalances,
  useMarketAssets,
  usePortfolioEstimateAssets,
  useUserTransactionHistory,
  useZar8YieldHistory,
  useZar8YieldPositions,
} from "../hooks/query/queries";
import Toaster from "../components/ui/toaster";
import InvestYieldEmptyState from "./InvestYieldEmptyState";
import Zar8YieldDashboardState from "./Zar8YieldDashboardState";
import Zar8LockFlow from "./Zar8LockFlow";
import Zar8UnlockFlow from "./Zar8UnlockFlow";
import { parseNumericValue } from "../utils/numberFormatter";
import CurrencyFormatter from "../utils/currencyFormatter";
import {
  calculateYieldPortfolioMetrics,
  getYieldMarketDataBySymbol,
} from "../utils/zar8Yield";
import {
  getZar8YieldHistoryFromResponse,
  getZar8YieldPositionsFromResponse,
  getZar8YieldTotalLockedFromResponse,
  mapZar8YieldHistoryToDashboardEntries,
  mapZar8YieldPositionsToLedgerEntries,
} from "../utils/zar8YieldApi";
import {
  clearZar8LockFlowState,
  createDefaultZar8LockFlowState,
  getZar8YieldFlowPath,
  persistZar8LockFlowState,
  readZar8LockFlowState,
  resolveZar8LockFlowState,
  ZAR8_LOCK_FLOW_STEPS,
  ZAR8_YIELD_FLOW_TYPES,
} from "../utils/zar8LockFlow";

export default function Zar8YieldDashboardContent({
  isPreview = false,
  basePath = "/zar8",
  flowPathBase = "/zar8",
  routeFlowType = null,
}) {
  const navigate = useNavigate();
  const { settings, userProfile } = useAuth();
  const userId = userProfile?._id;
  const formatter = useMemo(() => new CurrencyFormatter(), []);
  const [lockFlowState, setLockFlowState] = useState(() =>
    readZar8LockFlowState()
  );
  const resolvedFlowState = useMemo(
    () => resolveZar8LockFlowState(lockFlowState, routeFlowType),
    [lockFlowState, routeFlowType]
  );

  const { data: availableBalances } = useAvailableBalances(userId);
  const positionQueryParams = useMemo(
    () => ({
      status: "active",
      page: 1,
      limit: 100,
    }),
    []
  );
  const historyQueryParams = useMemo(
    () => ({
      page: 1,
      limit: 10,
    }),
    []
  );
  const { data: zar8YieldPositions } = useZar8YieldPositions(
    positionQueryParams,
    !!userId
  );
  const { data: zar8YieldHistory } = useZar8YieldHistory(
    historyQueryParams,
    !!userId
  );
  const { data: portfolioEstimateAssets } = usePortfolioEstimateAssets();
  const { data: userTransactions } = useUserTransactionHistory(userId, true, {
    fetchAllPages: false,
  });
  const { data: marketAssets } = useMarketAssets();

  const zar8Asset = useMemo(
    () =>
      availableBalances?.find(
        (asset) => asset?.asset?.toLowerCase() === "zar8"
      ) || null,
    [availableBalances]
  );

  const zar8Balance = parseNumericValue(zar8Asset?.availableAmount);
  const apiPositions = useMemo(
    () => getZar8YieldPositionsFromResponse(zar8YieldPositions),
    [zar8YieldPositions]
  );
  const totalLockedValue =
    getZar8YieldTotalLockedFromResponse(zar8YieldPositions);
  const totalZar8HoldingAmount = zar8Balance + totalLockedValue;
  const totalZar8HoldingValue = totalZar8HoldingAmount;
  const totalZar8Asset = useMemo(
    () =>
      zar8Asset
        ? {
            ...zar8Asset,
            availableAmount: totalZar8HoldingAmount,
            valueInLocalCurrency: totalZar8HoldingValue,
            valueInZar: totalZar8HoldingValue,
          }
        : {
            asset: "zar8",
            availableAmount: totalZar8HoldingAmount,
            valueInLocalCurrency: totalZar8HoldingValue,
            valueInZar: totalZar8HoldingValue,
          },
    [totalZar8HoldingAmount, totalZar8HoldingValue, zar8Asset]
  );
  const activeYieldEntries = useMemo(
    () => mapZar8YieldPositionsToLedgerEntries(apiPositions),
    [apiPositions]
  );
  const historyItems = useMemo(
    () => getZar8YieldHistoryFromResponse(zar8YieldHistory),
    [zar8YieldHistory]
  );
  const totalYieldValue = totalZar8HoldingValue;
  const hasYieldActivity =
    activeYieldEntries.length > 0 || historyItems.length > 0;

  const yieldMarketData = useMemo(
    () => getYieldMarketDataBySymbol(marketAssets, "ZAR8"),
    [marketAssets]
  );
  const totalPortfolioValue = parseNumericValue(
    portfolioEstimateAssets?.zarEstimate ??
      portfolioEstimateAssets?.portfolioEstimateRaw
  );
  const totalPortfolioValueWithLockedZar8 =
    totalPortfolioValue + totalLockedValue;
  const portfolioMetrics = useMemo(() => {
    const rawMetrics = calculateYieldPortfolioMetrics({
      transactions: userTransactions,
      assetSymbol: "ZAR8",
      assetBalance: totalZar8Asset,
      totalPortfolioValue: totalPortfolioValueWithLockedZar8,
    });
    const hasCostBasis =
      rawMetrics.netInvested > 0 ||
      rawMetrics.averageCost > 0 ||
      rawMetrics.totalDeposited > 0;

    if (hasCostBasis || totalZar8HoldingValue <= 0) {
      return rawMetrics;
    }

    return {
      ...rawMetrics,
      averageCost:
        totalZar8HoldingAmount > 0
          ? totalZar8HoldingValue / totalZar8HoldingAmount
          : 1,
      netInvested: totalZar8HoldingValue,
      unrealizedPnL: 0,
      portfolioDiversity:
        totalPortfolioValueWithLockedZar8 > 0
          ? (totalZar8HoldingValue / totalPortfolioValueWithLockedZar8) * 100
          : 0,
      totalPnL: 0,
      pnlPercentage: 0,
      totalDeposited: totalZar8HoldingValue,
    };
  }, [
    totalPortfolioValueWithLockedZar8,
    totalZar8Asset,
    totalZar8HoldingAmount,
    totalZar8HoldingValue,
    userTransactions,
  ]);
  const historyEntries = useMemo(
    () =>
      mapZar8YieldHistoryToDashboardEntries({
        history: historyItems,
        formatter,
      }),
    [formatter, historyItems]
  );

  useEffect(() => {
    if (resolvedFlowState?.isOpen) {
      persistZar8LockFlowState(resolvedFlowState);
      return;
    }

    clearZar8LockFlowState();
  }, [resolvedFlowState]);

  const handleOpenYieldFlow = (flowType = ZAR8_YIELD_FLOW_TYPES.lock) => {
    window.scrollTo(0, 0);
    setLockFlowState((currentState) => {
      const nextState = {
        ...createDefaultZar8LockFlowState(),
        ...currentState,
        isOpen: true,
        flowType,
        step:
          currentState?.step === ZAR8_LOCK_FLOW_STEPS.success
            ? ZAR8_LOCK_FLOW_STEPS.amount
            : currentState?.step || ZAR8_LOCK_FLOW_STEPS.amount,
      };
      navigate(getZar8YieldFlowPath(flowPathBase, flowType, basePath));
      return nextState;
    });
  };

  const handleOpenYieldCalculator = () => {
    window.scrollTo(0, 0);
    navigate(`${basePath.replace(/\/+$/, "")}/calculator`, {
      state: {
        returnTo: basePath,
      },
    });
  };

  const handleSwitchYieldFlowMode = (nextFlowType) => {
    window.scrollTo(0, 0);
    setLockFlowState({
      ...createDefaultZar8LockFlowState(),
      isOpen: true,
      flowType: nextFlowType,
    });
    navigate(getZar8YieldFlowPath(flowPathBase, nextFlowType, basePath));
  };

  const handleCloseLockFlow = () => {
    clearZar8LockFlowState();
    setLockFlowState(createDefaultZar8LockFlowState());
    window.scrollTo(0, 0);
    navigate(basePath);
  };

  if (resolvedFlowState?.isOpen) {
    return (
      <main>
        <Toaster />
        {resolvedFlowState.flowType === ZAR8_YIELD_FLOW_TYPES.unlock ? (
          <Zar8UnlockFlow
            flowState={resolvedFlowState}
            onFlowStateChange={setLockFlowState}
            activeYieldEntries={activeYieldEntries}
            zar8Asset={zar8Asset}
            isTwoFactorEnabled={Boolean(
              settings?.security?.twoFactorAuth?.enabled
            )}
            onClose={handleCloseLockFlow}
            onModeChange={handleSwitchYieldFlowMode}
          />
        ) : (
          <Zar8LockFlow
            flowState={resolvedFlowState}
            onFlowStateChange={setLockFlowState}
            availableBalanceZar={zar8Balance}
            zar8Asset={zar8Asset}
            isTwoFactorEnabled={Boolean(
              settings?.security?.twoFactorAuth?.enabled
            )}
            onClose={handleCloseLockFlow}
            onModeChange={handleSwitchYieldFlowMode}
          />
        )}
      </main>
    );
  }

  if (!hasYieldActivity && totalYieldValue <= 0) {
    return (
      <main>
        <Toaster />
        <InvestYieldEmptyState
          totalYieldValue={totalYieldValue}
          assetBalance={totalZar8HoldingAmount}
          asset={totalZar8Asset}
          assetSymbol="ZAR8"
          assetName="ZAR8"
          marketData={yieldMarketData}
          onManage={handleOpenYieldFlow}
          onCalculator={handleOpenYieldCalculator}
          onStart={() => navigate("/trading/digitalWallet")}
          isPreview={isPreview}
        />
      </main>
    );
  }

  return (
    <main>
      <Toaster />
      <Zar8YieldDashboardState
        totalYieldValue={totalYieldValue}
        assetBalance={totalZar8HoldingAmount}
        asset={totalZar8Asset}
        assetSymbol="ZAR8"
        assetName="ZAR8"
        marketData={yieldMarketData}
        portfolioMetrics={portfolioMetrics}
        historyEntries={historyEntries}
        onManage={handleOpenYieldFlow}
        onCalculator={handleOpenYieldCalculator}
        onStart={() => navigate("/trading/digitalWallet")}
        isPreview={isPreview}
      />
    </main>
  );
}
